/**
 * CartIconBadge.tsx
 * --------------------------------------------------------------------------
 * Composición visual de un icono `ShoppingBag` (lucide) con un pequeño
 * badge dorado que muestra el `itemCount` del carrito. El badge solo se
 * renderiza cuando `itemCount > 0`. Si la cifra excede 99, se muestra
 * "99+" para evitar overflow.
 *
 * El componente es un Link a `/cuenta/carrito` para que actúe como acceso
 * rápido desde el chrome global. El conteo proviene del store `useCart`.
 *
 * Solo tokens del sistema. ARIA describe el estado en SR-friendly.
 */
import { Link } from 'react-router-dom';
import { ShoppingBag } from 'lucide-react';
import { Routes } from '../../lib/routes';
import { cn } from '../../lib/cn';
import { useCartItemCount } from '../../features/cart/useCart';

// Tipos exportados ----------------------------------------------------------

export interface CartIconBadgeProps {
  /** Clase opcional para sobreescribir tamaños / colores desde el padre. */
  className?: string;
  /** Etiqueta accesible base (sin el conteo). Default: "Abrir mi bolsa". */
  ariaLabelBase?: string;
  /**
   * Tono visual del icono según el fondo bajo el Navbar:
   *  - `light-bg` (default): icono violeta sobre fondo crema (resto del sitio).
   *  - `dark-bg`: icono crema sobre fondo violeta (hero, CTA banda, etc.).
   * El badge dorado se mantiene en ambos tonos (alto contraste sobre ambos).
   */
  tone?: 'light-bg' | 'dark-bg';
}

/** Cap visual para el badge — sobre 99 mostramos "99+". */
function formatCount(count: number): string {
  if (count > 99) return '99+';
  return String(count);
}

// Componente ----------------------------------------------------------------

export function CartIconBadge({
  className,
  ariaLabelBase = 'Abrir mi bolsa',
  tone = 'light-bg',
}: CartIconBadgeProps) {
  const itemCount = useCartItemCount();
  const hasItems = itemCount > 0;

  // Construimos un label dinámico que las SR puedan leer cómodamente.
  // Ej.: "Abrir mi bolsa, 3 productos" / "Abrir mi bolsa, vacía".
  const ariaLabel = hasItems
    ? `${ariaLabelBase}, ${itemCount} ${itemCount === 1 ? 'producto' : 'productos'}`
    : `${ariaLabelBase}, vacía`;

  const toneClasses =
    tone === 'dark-bg'
      ? 'text-cream-50 hover:bg-cream-100/15'
      : 'text-primary-700 hover:bg-cream-200';

  return (
    <Link
      to={Routes.accountCart}
      aria-label={ariaLabel}
      className={cn(
        // Igualamos altura/ancho al resto de iconos del header (44px,
        // touch target WCAG 2.5.5).
        'relative inline-flex items-center justify-center',
        'h-11 w-11 rounded-pill',
        'transition-colors duration-2 ease-out',
        toneClasses,
        className,
      )}
    >
      <ShoppingBag className="h-5 w-5" aria-hidden="true" strokeWidth={1.5} />
      {hasItems ? (
        <span
          // El badge es decorativo — el conteo ya está en el aria-label.
          aria-hidden="true"
          className={cn(
            'absolute -top-1 -right-1',
            'inline-flex items-center justify-center',
            'min-w-[18px] h-[18px] px-1',
            'rounded-pill',
            'bg-gold-500 text-primary-900',
            'text-[10px] font-semibold leading-none',
            'shadow-elev1',
          )}
        >
          {formatCount(itemCount)}
        </span>
      ) : null}
    </Link>
  );
}
